import { useState } from "react";
import styled from "styled-components";
import { lightTheme } from "../../styles/theme";
import { Input } from "../../components/Input";
import { Button } from "../../components/Button";
import { openInNewTab } from "../../utils/openInNewTab";
import { useTranslation } from "../../lang/TranslationContext";

export const FAQ = () => {
  const { translations } = useTranslation();
  const [open, setOpen] = useState<number | null>(null);
  const [subject, setSubject] = useState("");
  const [body, setBody] = useState("");

  const questions = [
    {
      q: translations.landing.faq.question1,
      a: translations.landing.faq.answer1,
    },
    {
      q: translations.landing.faq.question2,
      a: translations.landing.faq.answer2,
    },
    {
      q: translations.landing.faq.question3,
      a: translations.landing.faq.answer3,
    },
    {
      q: translations.landing.faq.question4,
      a: translations.landing.faq.answer4,
    },
    {
      q: translations.landing.faq.question5,
      a: translations.landing.faq.answer5,
    },
    {
      q: translations.landing.faq.question6,
      a: translations.landing.faq.answer6,
    },
    {
      q: translations.landing.faq.question7,
      a: translations.landing.faq.answer7,
    },
    {
      q: translations.landing.faq.question8,
      a: translations.landing.faq.answer8,
    },
  ];

  const buildMailLink = () => {
    const subjectText = encodeURIComponent(subject);
    const bodyText = encodeURIComponent(body);
    return `mailto:support@marketecho.io?subject=${subjectText}&body=${bodyText}`;
  };

  return (
    <Wrap>
      <InnerWrap>
        <div>
          <h1>FAQ:</h1>
          <p>{translations.landing.faq.subheading}</p>
          <FAQWrap>
            {questions.map((item, idx) => (
              <AccordionItem
                key={idx}
                open={open === idx}
                onClick={() => setOpen(open === idx ? null : idx)}
              >
                <Question>
                  {item.q}
                  <Chevron open={open === idx} className="pi pi-chevron-up" />
                </Question>
                <Answer open={open === idx}>{item.a}</Answer>
              </AccordionItem>
            ))}
          </FAQWrap>
        </div>
        <ContactForm>
          <div>
            <h1>{translations.landing.faq.contactHeading}</h1>
            <Input
              label={translations.landing.faq.subjectLabel}
              placeholder={translations.landing.faq.subjectPlaceholder}
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
            />
            <Input
              label={translations.landing.faq.messageLabel}
              placeholder={translations.landing.faq.messagePlaceholder}
              value={body}
              onChange={(e) => setBody(e.target.value)}
            />
            <StyledButton
              onClick={() => openInNewTab(buildMailLink())}
              text={translations.landing.faq.sendMessage}
              disabled={!subject || !body}
            />
          </div>
          <ORText>{translations.landing.faq.or}</ORText>
          <StyledButton
            onClick={() => openInNewTab("https://t.me/marketechosupport")}
            text={translations.landing.faq.contactTelegram}
          />
        </ContactForm>
      </InnerWrap>
    </Wrap>
  );
};

const ORText = styled.p`
  width: 100%;
  text-align: center;
  font-weight: bold;
  margin-top: 20px;
  font-size: 16px;
`;

const StyledButton = styled(Button)`
  width: 100%;
  margin-top: 20px;
`;

const ContactForm = styled.div`
  min-width: 300px;
  width: 385px;
  height: 548px;
  margin-top: 65px;
  padding: 24px;
  background: #fff;
  border-radius: 20px;
  box-shadow: 0 16px 32px -4px rgba(12, 12, 13, 0.1),
    0 4px 4px -4px rgba(12, 12, 13, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: flex-end;

  > div {
    width: 100%;

    h1 {
      width: 100%;
      text-align: center;
      font-size: 32px;
      color: ${lightTheme.typography.primary};
      font-weight: 600;
    }

    > p {
      margin-top: 16px;
    }
  }

  @media screen and (max-width: 700px) {
    width: 100%;
  }
`;

const Wrap = styled.div`
  width: 100%;
  padding: 115px 20px;
  display: flex;
  justify-content: center;

  @media screen and (max-width: 700px) {
    padding: 40px 20px;
  }
`;

const InnerWrap = styled.div`
  width: 100%;
  max-width: 1440px;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  gap: 100px;

  @media screen and (max-width: 700px) {
    flex-direction: column;
    gap: 20px;
  }

  > div {
    &:nth-of-type(1) {
      h1 {
        font-size: 36px;
        color: ${lightTheme.typography.primary};
        font-weight: 600;
      }
      p {
        max-width: 685px;
        margin-top: 12px;
        margin-bottom: 32px;
        font-size: 16px;
        font-weight: 400;
        color: ${lightTheme.typography.tetriary};
      }
    }
  }
`;

const FAQWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const AccordionItem = styled.div<{ open: boolean }>`
  background: #fff;
  border-radius: 12px;
  box-shadow: 0px 4px 6px -1px rgba(0, 0, 0, 0.1),
    0px 2px 4px -1px rgba(0, 0, 0, 0.06);
  cursor: pointer;
  transition: 0.2s;
  padding: 0;
  overflow: hidden;
  max-width: 700px;
`;

const Question = styled.div`
  padding: 10px 12px;
  font-size: 16px;
  font-weight: 500;
  color: ${lightTheme.typography.primary};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Chevron = styled.i<{ open: boolean }>`
  margin-left: 16px;
  transition: 0.2s;
  transform: ${({ open }) => (open ? "rotate(0deg)" : "rotate(180deg)")};
  font-size: 14px;
  color: ${lightTheme.typography.tetriary};
`;

const Answer = styled.div<{ open: boolean }>`
  max-height: ${({ open }) => (open ? "" : "0")};
  opacity: ${({ open }) => (open ? 1 : 0)};
  transition: 0.2s;
  padding: ${({ open }) => (open ? "10px 32px 22px 32px" : "0 32px")};
  font-size: 16px;
  color: #444;
  pointer-events: ${({ open }) => (open ? "auto" : "none")};
`;
